import {createModel} from '@rematch/core';

import {
  ImagesPromoVariants,
  createCommonPromotion,
  UpdateImagePromotionBody,
  fetchPromotionById,
  updateImagePromotion,
  checkUrl,
} from '~/services/hot-api/promotions';
import {PromotionTextTypes} from '~/utils/constants';
import {ImagePromotion, Locations} from '~/services/hot-api';
import {getProfileByContacts, ProfileType} from '~/services/promotions';

import type {RootModel} from '..';

export interface ImagePromotionState {
  promotion: ImagePromotion | null;
}

export interface ImagePromotionsState {
  autoRenew: boolean;
  type: ImagesPromoVariants | null;
  images: string[];
  url: string;
  mainForm: {
    phone: string;
    onlyfans: string;
  };
  selectedLocations: number[];
  selectedCounties: number[];
  selectedCategories: number[];
}

export interface UpdateImagePromotionParams {
  id: number;
  thumbnails: string[];
  url: string;
  display_url: string;
  title: string;
  description: string;
  photos: string[];
  autoRenew: boolean;
  locations: Locations;
  categories: number[];
  bids?: {
    cpc: string;
    daily: string;
  };
}

const defaultState: ImagePromotionState = {
  promotion: null,
};

export const imagePromotion = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setPayload: (state: ImagePromotionState, payload: Partial<ImagePromotionState>) => ({
      ...state,
      ...payload,
    }),
  },
  effects: (dispatch) => {
    return {
      getPromotionById: async (id: string) => {
        try {
          const res = await fetchPromotionById(id);

          dispatch.imagePromotion.setPayload({
            promotion: res.data as ImagePromotion,
          });
        } catch (err) {
          console.error(err);
        }
      },
      resetMainStep: () => {
        dispatch.urlPromotion.setPayload({
          type: null,
          profile: null,
          content: {
            website: '',
            headline: '',
            description: '',
          },
          selectedCategories: [],
          selectedLocations: [],
        });
      },
      updateImagePromotion: async (data: UpdateImagePromotionParams) => {
        const promotionBody: UpdateImagePromotionBody = {
          id: data.id,
          details: {
            title: data.title,
            description: data.description,
            display_url: data.display_url,
            thumb: data.thumbnails,
            website_url: data.url,
            images: data.photos,
            auto_renew: data.autoRenew,
          },
          geo: data.locations,
          category_ids: data.categories,
        };

        if (data.bids) {
          promotionBody.bids = {
            price: parseFloat(data.bids.cpc),
            daily_limit: parseFloat(data.bids.daily),
          };
        }

        try {
          await updateImagePromotion(promotionBody);
        } catch (err) {
          console.error(err);
        }
      },
      createPromotion: async (values: any) => {
        await createCommonPromotion({
          ...values,
          type: PromotionTextTypes.Image,
        });
      },
      checkUrlChangePossibility: async ({
        url,
        imageAdType,
      }: {
        url: string;
        imageAdType: ProfileType;
      }) => {
        const {data} = await getProfileByContacts({profile_url: url, ad: 'image'});
        if (data.check_url.notFound) {
          return {success: false, message: 'Ad URL you provided is not found'};
        }
        if (data.check_url.notAccessible) {
          return {success: false, message: 'Ad URL you provided is not accessible'};
        }
        if (data.check_url.redirectToExternal) {
          return {success: false, message: 'Ad URL you provided redirects to external site'};
        }

        const hasProfiles = data.profiles && data.profiles.length > 0;

        // Если мы нашли профайлы по url
        if (data.check_url.success) {
          if (hasProfiles) {
            // Мы сверяем тип каждого найденного объекта
            for (let i = 0; i < data.profiles.length; i += 1) {
              const profile = data.profiles[i];
              if (profile.type === imageAdType) {
                return {
                  success: true,
                  profiles: [data.profiles[i]],
                  message: 'URL is valid and can be updated.',
                  isEscortCategory: data.check_url.isEscortCategory,
                  siteId: profile.site_id,
                };
              }
            }

            return {
              success: false,
              profiles: [],
              message:
                'The provided link is not a refer to your ad type. Please provide a correct link.',
            };
          }

          // Если не нашли профили, но тип объявления UnknownUrl,
          // то юзер можем изменить ссылку на другую с типом UnknownUrl
          if (imageAdType === ProfileType.Unknown) {
            return {
              success: true,
              profiles: [],
              message:
                "Sorry, we haven't indexed your page yet. Once your page will be indexed you'll receive notification. This may take up to 24 hours.",
              siteId: null,
            };
          }

          // В ином случае считаем что ссылка не совпадает по типу
          return {
            success: false,
            profiles: [],
            message:
              'The provided link is not a refer to your ad type. Please provide a correct link.',
          };
        }

        return {
          success: false,
          profiles: [],
          message:
            'The provided link is not a refer to your ad type. Please provide a correct link.',
        };
      },
    };
  },
});
